// src/components/BookDetail.jsx - DYNAMIC FETCH
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/BookDetail.css";
import { apiUrl } from "../lib/api";
import { withLanguageMetadata } from "../data/bookLanguages";

const API_BASE_URL = apiUrl('/api/books');

export default function BookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // NOTE: Sample data has been removed entirely.

  useEffect(() => {
    const fetchBookDetails = async () => {
      // Ensure we have an ID before fetching
      if (!id) {
        setLoading(false);
        setBook(null);
        return;
      }
      
      setLoading(true);
      
      try {
        // Fetch specific book details using the ID in the URL path
        const response = await fetch(`${API_BASE_URL}/${id}`);
        
        if (response.status === 404) {
          setBook(null); 
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to load book: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log(`✅ Book ID ${id} loaded from D1 API.`, data);
        
        setBook(withLanguageMetadata(data)); 

      } catch (error) {
        console.error(`❌ Error loading book ID ${id}:`, error);
        // Display a generic error message if fetch fails
        setBook({ 
            title: "Error Loading Book", 
            description: "Could not connect to the database or fetch book details.", 
            image_url: null, 
            pdf_url: null 
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchBookDetails();
    
  }, [id]); // Re-run effect if the ID changes

  const onImgError = (e) => {
    // ... (keep your existing image error handler) ...
    const el = e.currentTarget;
    if (!el.dataset.fallback) {
      el.dataset.fallback = "1";
      el.src = "/books/images/placeholder.jpg";
    } else {
      el.style.display = "none";
      const placeholder = document.createElement('div');
      placeholder.className = 'bookdetail__cover-placeholder';
      placeholder.textContent = `Cover: ${book?.title || 'Book'}`;
      el.parentNode.appendChild(placeholder);
    }
  };

  const openEdition = (url) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleBackToBooks = () => {
    navigate('/books');
  };

  // --- JSX RENDER LOGIC ---

  if (loading) {
    return (
      <section className="bookdetail">
        <div style={{ textAlign: 'center', color: '#ffcc00', padding: '4rem', fontSize: '1.2rem' }}>
          Loading book details for ID: {id} from D1...
        </div>
      </section>
    );
  }

  if (!book || book.title === "Error Loading Book") {
    // Note: Reusing the existing error/not found display logic
    return (
      <section className="bookdetail">
        <div style={{ textAlign: 'center', color: '#ffcc00', padding: '4rem' }}>
          <h1 className="bookdetail__title">{book?.title || "Book Not Found"}</h1>
          <p>{book?.description || `Book ID: "${id}" not found in our database.`}</p>
          <button 
            onClick={handleBackToBooks}
            className="bookdetail__btn"
            style={{ marginTop: '2rem' }}
          >
            ← Back to All Books
          </button>
        </div>
      </section>
    );
  }
  
  // The main successful rendering of the book details
  return (
    <section className="bookdetail" aria-labelledby="bookdetail-title">
      <header className="bookdetail__header">
        <h1 id="bookdetail-title" className="bookdetail__title">
          {book.title}
        </h1>
        {book.arabicTitle && <p className="bookdetail__arabic-title" lang="ar" dir="rtl">{book.arabicTitle}</p>}

        <ul className="bookdetail__meta" aria-label="Book details">
          {book.author && <li><strong>Author:</strong> {book.author}</li>}
          {book.year && <li><strong>Published:</strong> {book.year}</li>}
          {book.genre && <li><strong>Genre:</strong> {book.genre}</li>}
          {book.pages && <li><strong>Length:</strong> {book.pages} pages</li>}
        </ul>
      </header>

      <div className="bookdetail__body">
        <div className="bookdetail__coverwrap">
          <img
            src={book.image_url || "/books/images/placeholder.jpg"}
            alt={`Cover of ${book.title}`}
            className="bookdetail__cover"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        <div className="bookdetail__text">
          <div className="bookdetail__description">
            <h3>About This Book</h3>
            <p className="bookdetail__desc">{book.description}</p>
          </div>

          <div className="bookdetail__actions">
            <button
              className="bookdetail__btn"
              onClick={() => openEdition(book.arabicPdfUrl)}
              disabled={!book.arabicPdfUrl}
            >
              Arabic / العربية
            </button>
            <button
              className="bookdetail__btn bookdetail__btn--ghost"
              onClick={() => openEdition(book.englishPdfUrl)}
              disabled={!book.englishPdfUrl}
              title={!book.englishPdfUrl ? 'English translation in preparation' : 'Open English edition'}
            >
              {book.englishPdfUrl ? 'English' : 'English — In preparation'}
            </button>
          </div>

          <div className="bookdetail__info">
            <h3>Book Information</h3>
            <p><strong>Format:</strong> PDF Document</p>
            <p><strong>File Size:</strong> N/A (Dynamic Fetch)</p>
            <p><strong>Original language:</strong> Arabic</p>
            <p><strong>English edition:</strong> {book.englishPdfUrl ? 'Available' : 'In preparation'}</p>
            <p><strong>Availability:</strong> Free Access</p>
          </div>

          <div className="bookdetail__actions">
            <button 
              className="bookdetail__btn bookdetail__btn--ghost"
              onClick={handleBackToBooks}
            >
              ← Back to All Books
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
